import { MediaCarousel } from 'senza-theme';

class SenzaMediaCarousel extends MediaCarousel {
  connectedCallback() {
    super.connectedCallback();
    this._syncLoop();
    this._loopWidth = this.clientWidth;
    this._loopResize = new ResizeObserver(() => {
      if (this.clientWidth === this._loopWidth) return;
      this._loopWidth = this.clientWidth;
      this._syncLoop();
    });
    this._loopResize.observe(this);
    this._desktopQuery = matchMedia('(min-width: 1000px)');
    this._desktopQuery.addEventListener('change', () => this._syncLoop(), { signal: this._abortController.signal });
  }

  disconnectedCallback() {
    this._loopResize?.disconnect();
    cancelAnimationFrame(this._loopFrame);
    this._removeClones();
    super.disconnectedCallback();
  }

  get loop() {
    return Boolean(this._loopEnabled);
  }

  _removeClones() {
    this.querySelectorAll('[data-senza-loop-clone]').forEach((slide) => slide.remove());
    this._loopEnabled = false;
  }

  _syncLoop() {
    cancelAnimationFrame(this._loopFrame);
    this._normalizing = false;
    this._removeClones();
    const originals = this.visibleItems;
    const carouselLayout = !matchMedia('(min-width: 1000px)').matches || this.getAttribute('desktop-mode')?.includes('carousel');
    if (!carouselLayout || originals.length < 2) return;
    const first = originals[0];
    const last = originals[originals.length - 1];
    if (!first.querySelector('img') || !last.querySelector('img')) return;

    const snapshot = (source) => {
      const clone = source.cloneNode(false);
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.classList.remove('is-selected');
      clone.setAttribute('data-senza-loop-clone', this.items.indexOf(source));
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      const image = source.querySelector('img').cloneNode(true);
      image.removeAttribute('id');
      image.loading = 'eager';
      image.alt = '';
      clone.append(image);
      return clone;
    };

    this._leadingClone = snapshot(last);
    this._trailingClone = snapshot(first);
    this.prepend(this._leadingClone);
    this.append(this._trailingClone);
    this._loopEnabled = true;
    this._hasPendingProgrammaticScroll = false;
    if (!originals.includes(this.selectedSlide)) this._selectedIndex = this.items.indexOf(first);
    this._jumpTo(this.selectedSlide);
  }

  _filterItems(event) {
    this._removeClones();
    super._filterItems(event);
    this._syncLoop();
  }

  _slidePosition(slide) {
    const styles = getComputedStyle(this);
    const position = slide.offsetLeft;
    if (this._extractSlideAlign(slide) === 'center') {
      const paddingStart = parseFloat(styles.scrollPaddingInlineStart) || 0;
      const paddingEnd = parseFloat(styles.scrollPaddingInlineEnd) || 0;
      return position - (this.clientWidth - slide.clientWidth) / 2 + (paddingEnd - paddingStart) / 2;
    }
    return position - (parseFloat(styles.scrollPaddingInlineStart) || 0);
  }

  _jumpTo(slide) {
    this._normalizing = true;
    const snap = this.style.scrollSnapType;
    this.style.scrollSnapType = 'none';
    this.scrollTo({ left: this._slidePosition(slide), behavior: 'instant' });
    this.style.scrollSnapType = snap;
    cancelAnimationFrame(this._loopFrame);
    this._loopFrame = requestAnimationFrame(() => { this._normalizing = false; });
  }

  _nearestSlide() {
    const physicalSlides = [this._leadingClone, ...this.visibleItems, this._trailingClone];
    return physicalSlides.reduce((nearest, slide) =>
      Math.abs(this._slidePosition(slide) - this.scrollLeft) < Math.abs(this._slidePosition(nearest) - this.scrollLeft) ? slide : nearest
    );
  }

  _logicalIndex(slide) {
    return slide.hasAttribute('data-senza-loop-clone') ? Number(slide.getAttribute('data-senza-loop-clone')) : this.items.indexOf(slide);
  }

  _selectLogical(index) {
    const changed = index !== this.selectedIndex;
    this._selectedIndex = index;
    this.items.forEach((slide, i) => slide.classList.toggle('is-selected', i === index));
    if (changed) {
      this._dispatchEvent('carousel:select', index);
      this._dispatchEvent('carousel:change', index);
    }
  }

  select(index, { direction, animate = true, force = false } = {}) {
    if (!this._loopEnabled) return super.select(index, { animate, force });
    if (this._hasPendingProgrammaticScroll && !force) return;
    const target = this.items[index];
    const visible = this.visibleItems;
    if (!visible.includes(target) || (index === this.selectedIndex && !force)) return;
    let destination = target;
    if (direction === 'next' && this.selectedSlide === visible[visible.length - 1] && target === visible[0]) {
      destination = this._trailingClone;
    } else if (direction === 'previous' && this.selectedSlide === visible[0] && target === visible[visible.length - 1]) {
      destination = this._leadingClone;
    }
    this._selectLogical(index);
    const smooth = animate && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._hasPendingProgrammaticScroll = smooth;
    if (smooth) {
      this.scrollTo({ left: this._slidePosition(destination), behavior: 'smooth' });
    } else {
      this._jumpTo(target);
      this._dispatchEvent('carousel:settle', index);
    }
  }

  _onCarouselScroll() {
    if (!this._loopEnabled) return super._onCarouselScroll();
    if (this._normalizing || this._hasPendingProgrammaticScroll) return;
    this._selectLogical(this._logicalIndex(this._nearestSlide()));
  }

  _onScrollSettled() {
    if (!this._loopEnabled) return super._onScrollSettled();
    if (this._normalizing) return;
    const physicalSlide = this._nearestSlide();
    const index = this._logicalIndex(physicalSlide);
    this._hasPendingProgrammaticScroll = false;
    this._selectLogical(index);
    if (physicalSlide.hasAttribute('data-senza-loop-clone')) this._jumpTo(this.items[index]);
    this._dispatchEvent('carousel:settle', index);
  }
}

if (!customElements.get('senza-media-carousel')) customElements.define('senza-media-carousel', SenzaMediaCarousel);
